<template>
  <div class="ka-pay">
    <div class="ka-pay__header">
      <h3 class="ka-pay__title">Cash vs Card</h3>
      <p class="ka-pay__subtitle">Kiosk bookings by payment method</p>
    </div>

    <div class="ka-pay__total">
      <span class="ka-pay__total-value">{{ fmt(totalBookings) }}</span>
      <span class="ka-pay__total-label">paid bookings · {{ money(totalRevenue) }}</span>
    </div>

    <div
      class="ka-pay__bar"
      role="img"
      :aria-label="`Cash ${cash.share}%, Card ${card.share}% of bookings`"
    >
      <span
        v-if="totalBookings"
        class="ka-pay__seg"
        :style="{ width: cash.share + '%', background: colors.cash }"
      />
      <span
        v-if="totalBookings"
        class="ka-pay__seg"
        :style="{ width: card.share + '%', background: colors.card }"
      />
    </div>

    <div class="ka-pay__methods">
      <div v-for="m in [cash, card]" :key="m.key" class="ka-pay__method">
        <div class="ka-pay__method-top">
          <span class="ka-pay__icon" :style="{ background: m.tint, color: m.color }">
            <feather-icon :icon="m.icon" svgClasses="ka-pay__icon-svg" />
          </span>
          <span class="ka-pay__method-label">{{ m.label }}</span>
          <span class="ka-pay__method-share">{{ m.share }}%</span>
        </div>
        <div class="ka-pay__method-value">{{ fmt(m.bookings) }}</div>
        <div class="ka-pay__method-sub">bookings</div>
        <div class="ka-pay__method-rev">{{ money(m.revenue) }}</div>
        <div class="ka-pay__method-sub">revenue</div>
      </div>
    </div>
  </div>
</template>

<script>
import { COLORS, formatCurrency, formatNumber, pct } from "../kioskAnalyticsHelpers";

export default {
  name: "KioskPaymentSplit",
  props: {
    totals: { type: Object, required: true },
  },
  data() {
    return { colors: COLORS };
  },
  computed: {
    totalBookings() {
      return this.totals.cashBookings + this.totals.cardBookings;
    },
    totalRevenue() {
      return this.totals.cashRevenue + this.totals.cardRevenue;
    },
    cash() {
      return {
        key: "cash",
        label: "Cash",
        icon: "DollarSignIcon",
        color: COLORS.cash,
        tint: "rgba(14, 165, 233, 0.12)",
        bookings: this.totals.cashBookings,
        revenue: this.totals.cashRevenue,
        share: pct(this.totals.cashBookings, this.totalBookings),
      };
    },
    card() {
      return {
        key: "card",
        label: "Card",
        icon: "CreditCardIcon",
        color: COLORS.card,
        tint: "rgba(139, 92, 246, 0.12)",
        bookings: this.totals.cardBookings,
        revenue: this.totals.cardRevenue,
        share: pct(this.totals.cardBookings, this.totalBookings),
      };
    },
  },
  methods: {
    fmt: formatNumber,
    money: formatCurrency,
  },
};
</script>

<style scoped lang="scss">
.ka-pay {
  background: #fff;
  border-radius: 18px;
  padding: 1.35rem 1.5rem;
  border: 1px solid #ececf2;
  box-shadow: 0 1px 2px 0 rgba(20, 21, 34, 0.04);
  height: 100%;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;

  &__title {
    font-size: 1.05rem;
    font-weight: 700;
    color: #2d2d2d;
    margin: 0 0 0.3rem;
  }

  &__subtitle {
    font-size: 0.8125rem;
    color: #a8a8b3;
    margin: 0 0 1.25rem;
  }

  &__total {
    display: flex;
    align-items: baseline;
    gap: 0.5rem;
    margin-bottom: 1rem;
  }

  &__total-value {
    font-family: "Plus Jakarta Sans", sans-serif;
    font-size: 1.5rem;
    font-weight: 800;
    letter-spacing: -0.03em;
    color: #16172b;
    font-variant-numeric: tabular-nums;
  }

  &__total-label {
    font-size: 0.8125rem;
    color: #6b7280;
  }

  &__bar {
    display: flex;
    gap: 2px;
    height: 12px;
    border-radius: 999px;
    overflow: hidden;
    background: #f3f4f6;
    margin-bottom: 1.25rem;
  }

  &__seg {
    display: block;
    height: 100%;
    transition: width 0.4s ease;
  }

  &__methods {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
  }

  &__method {
    border: 1px solid #f0f0f4;
    border-radius: 14px;
    padding: 1rem;
    min-width: 0;
  }

  &__method-top {
    display: flex;
    align-items: center;
    gap: 0.55rem;
    margin-bottom: 0.75rem;
  }

  &__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border-radius: 9px;
  }

  &__icon-svg {
    width: 15px;
    height: 15px;
  }

  &__method-label {
    font-size: 0.875rem;
    font-weight: 600;
    color: #2d2d2d;
  }

  &__method-share {
    margin-left: auto;
    font-size: 0.8125rem;
    color: #a8a8b3;
    font-variant-numeric: tabular-nums;
  }

  &__method-value,
  &__method-rev {
    font-family: "Plus Jakarta Sans", sans-serif;
    font-weight: 800;
    color: #16172b;
    letter-spacing: -0.02em;
    font-variant-numeric: tabular-nums;
  }

  &__method-value {
    font-size: 1.4rem;
  }

  &__method-rev {
    font-size: 1.05rem;
    margin-top: 0.6rem;
  }

  &__method-sub {
    font-size: 0.72rem;
    color: #a8a8b3;
  }
}

@media (max-width: 480px) {
  .ka-pay__methods {
    grid-template-columns: 1fr;
  }
}
</style>
